<template>
  <div v-if="showBanner" class="subscription-banner">
    <div class="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg v-if="isTrial" class="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <svg v-else-if="isExpired" class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <svg v-else class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-blue-900 dark:text-blue-100">
            {{ bannerTitle }}
          </h3>
          <p class="text-sm text-blue-700 dark:text-blue-200">
            {{ bannerMessage }}
          </p>
        </div>
      </div>
      <div class="flex items-center space-x-3">
        <span class="text-sm font-semibold" :class="urgencyClass">
          {{ timeRemaining }}
        </span>
        <button
          @click="goToBilling"
          class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {{ actionButtonText }}
        </button>
        <button
          @click="dismissBanner"
          class="text-blue-400 hover:text-blue-600 dark:text-blue-300 dark:hover:text-blue-100"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';

export default {
  name: 'SubscriptionBanner',
  setup() {
    const { accountId } = useAccount();
    return { accountId };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
    }),
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
    subscription() {
      return this.currentAccount.subscription || {};
    },
    customAttributes() {
      return this.currentAccount.custom_attributes || {};
    },
    subscriptionStatus() {
      return this.subscription.status || this.customAttributes.subscription_status || '';
    },
    subscriptionEndsOn() {
      return (
        this.subscription.end_date ||
        this.subscription.trial_ends_at ||
        this.customAttributes.subscription_ends_on ||
        ''
      );
    },
    planName() {
      return this.subscription.plan_name || this.customAttributes.plan_name || '';
    },
    isTrial() {
      if (typeof this.subscription.is_trial === 'boolean') {
        return this.subscription.is_trial;
      }
      return this.subscriptionStatus === 'trial';
    },
    isActive() {
      if (typeof this.subscription.is_active === 'boolean') {
        return this.subscription.is_active;
      }
      return this.subscriptionStatus === 'active';
    },
    isExpired() {
      if (typeof this.subscription.is_expired === 'boolean') {
        return this.subscription.is_expired;
      }
      return this.subscriptionStatus === 'expired';
    },
    daysUntilExpiry() {
      if (this.isTrial) {
        const trialDays = this.subscription.days_until_trial_expiry;
        if (Number.isInteger(trialDays)) {
          return trialDays;
        }
      }

      const expiryDays = this.subscription.days_until_expiry;
      if (Number.isInteger(expiryDays)) {
        return expiryDays;
      }

      if (!this.subscriptionEndsOn) return null;
      const endDate = new Date(this.subscriptionEndsOn);
      const today = new Date();
      const diffTime = endDate - today;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    },
    showBanner() {
      // Show banner if:
      // 1. Trial expires in 7 days or less
      // 2. Active subscription expires in 7 days or less
      // 3. Subscription is expired
      if (this.isExpired) return true;
      if (this.daysUntilExpiry !== null && this.daysUntilExpiry <= 7) return true;
      return false;
    },
    bannerTitle() {
      if (this.isExpired) return 'Subscription đã hết hạn';
      if (this.isTrial) return 'Trial sắp hết hạn';
      return 'Subscription sắp hết hạn';
    },
    bannerMessage() {
      if (this.isExpired) {
        return `Subscription của bạn đã hết hạn. Một số tính năng có thể bị hạn chế.`;
      }
      if (this.isTrial) {
        if (this.daysUntilExpiry === null) {
          return 'Trial của bạn sắp hết hạn. Hãy nâng cấp để tiếp tục sử dụng.';
        }
        return `Trial của bạn sẽ hết hạn trong ${this.daysUntilExpiry} ngày. Hãy nâng cấp để tiếp tục sử dụng.`;
      }
      if (this.daysUntilExpiry === null) {
        return 'Subscription của bạn sắp hết hạn. Hãy gia hạn để tiếp tục sử dụng.';
      }
      return `Subscription của bạn sẽ hết hạn trong ${this.daysUntilExpiry} ngày. Hãy gia hạn để tiếp tục sử dụng.`;
    },
    timeRemaining() {
      if (this.isExpired) return 'Đã hết hạn';
      if (this.daysUntilExpiry === null) return 'Đang xử lý';
      if (this.daysUntilExpiry === 1) return '1 ngày';
      return `${this.daysUntilExpiry} ngày`;
    },
    urgencyClass() {
      if (this.isExpired) return 'text-red-600 dark:text-red-400';
      if (this.daysUntilExpiry === null) return 'text-blue-600 dark:text-blue-400';
      if (this.daysUntilExpiry <= 3) return 'text-red-600 dark:text-red-400';
      if (this.daysUntilExpiry <= 7) return 'text-orange-600 dark:text-orange-400';
      return 'text-blue-600 dark:text-blue-400';
    },
    actionButtonText() {
      if (this.isExpired) return 'Nâng cấp ngay';
      if (this.isTrial) return 'Nâng cấp';
      return 'Gia hạn';
    }
  },
  methods: {
    goToBilling() {
      this.$router.push('/app/accounts/settings/billing');
    },
    dismissBanner() {
      // Store dismissal in localStorage
      localStorage.setItem('subscription_banner_dismissed', Date.now().toString());
      this.$emit('dismiss');
    }
  },
  mounted() {
    // Check if banner was recently dismissed (within 24 hours)
    const dismissed = localStorage.getItem('subscription_banner_dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const now = Date.now();
      const hoursSinceDismissed = (now - dismissedTime) / (1000 * 60 * 60);
      
      if (hoursSinceDismissed < 24) {
        this.$emit('dismiss');
      }
    }
  }
};
</script>

<style scoped>
.subscription-banner {
  @apply mb-4;
}
</style>
